/**
 * NiV tool — the food promotion queue (nutrition clinics).
 *
 * Every food a coach adds to their own clinic catalog raises a request here.
 * Approving copies it into the global `foods` collection, through the same
 * normalizer the seed import uses, so every nutrition clinic gets it; rejecting
 * leaves the clinic's own copy untouched.
 */

import { useState } from "react";
import {
  Buildings,
  Check,
  ForkKnife,
  User,
  X,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import {
  useGetFoodPromotionRequestsQuery,
  useApproveFoodPromotionRequestMutation,
  useRejectFoodPromotionRequestMutation,
} from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import { formatNivDateTime } from "./NivSystemMessagesTool";
import NivFoodSeedPanel from "./NivFoodSeedPanel";

const STATUS_FILTERS = [
  { id: "pending", label: "ממתינות" },
  { id: "approved", label: "אושרו" },
  { id: "rejected", label: "נדחו" },
];

/** Macro chips shown on every card, in the order coaches read them. */
const MACRO_FIELDS = [
  { key: "calories", label: "קלוריות", unit: "" },
  { key: "carbohydrates", label: "פחמימות", unit: "ג׳" },
  { key: "protein", label: "חלבון", unit: "ג׳" },
  { key: "fat", label: "שומן", unit: "ג׳" },
];

function NivFoodRequestsTool({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const [status, setStatus] = useState("pending");
  const [rejectTarget, setRejectTarget] = useState(null);

  const { data: requests = [], isLoading } = useGetFoodPromotionRequestsQuery(
    { status },
    { skip: !isAuthorized }
  );

  const [approveRequest, { isLoading: approving }] =
    useApproveFoodPromotionRequestMutation();
  const [rejectRequest, { isLoading: rejecting }] =
    useRejectFoodPromotionRequestMutation();

  const handleApprove = async (request) => {
    try {
      const result = await approveRequest({ requestId: request.id }).unwrap();
      showSuccess(`"${result.foodName}" נוסף למאגר הכללי`);
    } catch (error) {
      console.error("[CK-10040] Error approving food promotion request:", {
        errorCode: "CK-10040",
        errorMessage: error?.message,
        requestId: request.id,
        timestamp: new Date().toISOString(),
      });
      // The normalizer's message is the useful part: it says which field failed
      showError(error?.message || "שגיאה באישור הבקשה");
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await rejectRequest({ requestId: rejectTarget.id }).unwrap();
      showSuccess("הבקשה נדחתה");
    } catch (error) {
      console.error("[CK-10041] Error rejecting food promotion request:", {
        errorCode: "CK-10041",
        errorMessage: error?.message,
        requestId: rejectTarget.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בדחיית הבקשה");
    } finally {
      setRejectTarget(null);
    }
  };

  return (
    <div className="nfr-content">
      <div className="niv-system-updates-header">
        <h3>
          <ForkKnife size={22} weight="fill" />
          בקשות הוספת מזון
        </h3>
        <span className="niv-system-updates-count">{requests.length} בקשות</span>
      </div>

      <NivFoodSeedPanel isAuthorized={isAuthorized} />

      <div className="nfr-filters">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`nfr-filter ${status === filter.id ? "active" : ""}`}
            onClick={() => setStatus(filter.id)}
            aria-pressed={status === filter.id}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <ContentLoader message="טוען בקשות..." />
      ) : requests.length === 0 ? (
        <div className="niv-empty-state">
          <ForkKnife size={48} weight="light" />
          <p>אין בקשות בסטטוס הזה</p>
        </div>
      ) : (
        <div className="nfr-list">
          {requests.map((request) => {
            const food = request.food || {};
            return (
              <div key={request.id} className="nfr-card">
                <div className="nfr-card-head">
                  <span className="nfr-card-name">{food.foodName || "ללא שם"}</span>
                  <span className="nfr-card-date">
                    {formatNivDateTime(request.createdAt)}
                  </span>
                </div>

                <div className="nfr-macros">
                  {MACRO_FIELDS.map(({ key, label, unit }) => (
                    <span key={key} className="nfr-macro">
                      <span className="nfr-macro-label">{label}</span>
                      <span className="nfr-macro-value">
                        {food[key] ?? "—"}
                        {unit}
                      </span>
                    </span>
                  ))}
                  <span className="nfr-per100">ל־100 גרם</span>
                </div>

                {Object.keys(food.sizeOptions || {}).length > 0 && (
                  <div className="nfr-units">
                    {Object.entries(food.sizeOptions).map(([unit, grams]) => (
                      <span key={unit} className="nfr-unit">
                        {unit} · {grams} ג׳
                      </span>
                    ))}
                  </div>
                )}

                <div className="nfr-meta">
                  <span className="nfr-meta-item">
                    <Buildings size={14} /> {request.clinicId}
                  </span>
                  {request.requestedByName && (
                    <span className="nfr-meta-item">
                      <User size={14} /> {request.requestedByName}
                    </span>
                  )}
                </div>

                {status === "pending" && (
                  <div className="nfr-actions">
                    <button
                      type="button"
                      className="nfr-action nfr-action--approve"
                      onClick={() => handleApprove(request)}
                      disabled={approving || rejecting}
                    >
                      <Check size={16} weight="bold" />
                      אשר למאגר הכללי
                    </button>
                    <button
                      type="button"
                      className="nfr-action nfr-action--reject"
                      onClick={() => setRejectTarget(request)}
                      disabled={approving || rejecting}
                    >
                      <X size={16} weight="bold" />
                      דחה
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
        title="דחיית בקשה"
        message="המזון לא יתווסף למאגר הכללי. הוא יישאר זמין למרפאה שהוסיפה אותו."
        confirmText="דחה"
        cancelText="ביטול"
        type="danger"
      />
    </div>
  );
}

export default NivFoodRequestsTool;
